import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { IconZoomIn, IconZoomOut, IconEye, IconEyeOff, IconRotate, IconArrowsMaximize, IconArrowsMinimize, IconHandGrab } from '@tabler/icons-react';
import { lexicalToHtml, replacePlaceholders } from '../../utils/lexicalToHtml';
import styles from './DocPreviewPanel.module.scss';
import { useHotkeys } from '@/lib/hooks';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

/** Small icon button for the toolbar */
function TbBtn({ title, onClick, active = false, children }) {
  const btn = (
    <button
      onClick={onClick}
      className={cn(
        'w-7 h-7 inline-flex items-center justify-center rounded-full transition-colors',
        active
          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
          : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700'
      )}
    >
      {children}
    </button>
  );
  if (!title) return btn;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{btn}</TooltipTrigger>
      <TooltipContent side="top">{title}</TooltipContent>
    </Tooltip>
  );
}

const R2_URL = import.meta.env.VITE_R2_PUBLIC_URL || '';

// Denomination → stamp color, Bengali digit, Bengali words
const STAMP_CONFIG = {
  50:  { color: '#C96C5B', bgColor: '#ffefb0', amountBn: '৫০',  words: 'পঞ্চাশ টাকা' },
  100: { color: '#1f6b3d', bgColor: '#BED6B2', amountBn: '১০০', words: 'একশত টাকা' },
};
const STAMP_BOTTOM_TEXT = '“দেশপ্রেমের শপথ নিন, দুর্নীতিকে বিদায় দিন”';

const PAGE_SIZES = {
  A4: { width: 794, height: 1123 },
  A3: { width: 1123, height: 1587 },
  A5: { width: 559, height: 794 },
  LETTER: { width: 816, height: 1056 },
  LEGAL: { width: 816, height: 1344 }
};

const PAGE_MARGINS = {
  narrow: { top: 48, bottom: 48, left: 36, right: 36 },
  normal: { top: 72, bottom: 72, left: 72, right: 72 },
  moderate: { top: 60, bottom: 60, left: 54, right: 54 },
  wide: { top: 96, bottom: 96, left: 96, right: 96 },
  stamp: { top: 216, bottom: 72, left: 18, right: 18 }
};

// Convert 0–9 to Bengali digits — used for page numbering labels
const toBn = n => String(n).replace(/[0-9]/g, d => '০১২৩৪৫৬৭৮৯'[d]);

const PAGE_NUM_STYLE = {
  fontFamily: "'Kalpurush', 'Noto Sans Bengali', 'SolaimanLipi', Arial, sans-serif",
  fontSize: '14px',
  color: '#222',
  textAlign: 'center',
  lineHeight: '1.4',
  pointerEvents: 'none',
  position: 'absolute',
  left: 0,
  right: 0,
  zIndex: 10,
};

// Split HTML at page-break markers into segments (one per visual page)
function splitIntoPages(html) {
  if (!html) return [null];
  const parts = html.split(/<div[^>]*class="doc-page-break"[^>]*>[^<]*<\/div>/gi);
  return parts.length > 0 ? parts : [html];
}

// Join page segments back with a real CSS page-break div between them (for html2pdf)
function joinForExport(pages) {
  return pages
    .map((html, i) =>
      i === 0
        ? (html || '')
        : `<div style="page-break-before:always;break-before:page;height:0;margin:0;padding:0;"></div>${html || ''}`
    )
    .join('');
}

export default function DocPreviewPanel({
  templateDoc,
  formValues = {},
  activeSlug,
  pageRef,
  isExporting = false,
  placeholders: placeholdersConfig = {},
  refreshKey = 0,
  showStamp,          // bool | undefined — overrides md.stamp when provided
  stampDenomination = 50,
  hideExportToggle = false,
  hideToolbar = false,
  sideBySide = false,
  isEditMode = false,
  onContentChange = null,
  initialHtml = null,
  hideHighlights = false,
}) {
  const { t } = useTranslation();
  const panelRef        = useRef(null);
  const baseHtml        = useRef('');
  const debounceId      = useRef(null);
  const formValuesRef   = useRef(formValues);
  const placeholdersRef = useRef(placeholdersConfig);
  const editContentRef  = useRef(null);
  const pageRefsArr     = useRef([]);
  const dragRef         = useRef({ active: false, startX: 0, startY: 0, sl: 0, st: 0 });
  const fsDragRef       = useRef({ active: false, startX: 0, startY: 0, sl: 0, st: 0 });
  const isPanModeRef    = useRef(false);

  // Keep refs in sync with latest props so async fetch callbacks never use stale closures
  useEffect(() => { formValuesRef.current = formValues; });
  useEffect(() => { placeholdersRef.current = placeholdersConfig; });
  const isEditModeRef = useRef(isEditMode);
  useEffect(() => { isEditModeRef.current = isEditMode; }, [isEditMode]);

  const PAGE_BREAK_MARKER = '<div class="doc-page-break"></div>';
  const handleEditInput = useCallback(() => {
    if (!onContentChange || !isEditMode) return;
    const htmlParts = pageRefsArr.current
      .filter(Boolean)
      .map(el => el.innerHTML);
    onContentChange(htmlParts.join(PAGE_BREAK_MARKER));
  }, [onContentChange, isEditMode]);

  // In manual edit mode: clicking an unfilled placeholder clears it so user can type directly
  const handlePlaceholderClick = useCallback((e) => {
    const target = e.target.closest('.doc-value-unfilled');
    if (!target) return;
    e.preventDefault();
    target.textContent = '';
    target.classList.remove('doc-value-unfilled');
    // Place cursor inside the now-empty span
    const range = document.createRange();
    const sel = window.getSelection();
    range.setStart(target, 0);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }, []);

  const fullscreenPanelRef = useRef(null);

  const [loading,          setLoading]          = useState(false);
  const [displayHtml,      setDisplayHtml]      = useState('');
  const [autoZoom,         setAutoZoom]         = useState(1.0);
  const [zoomOffset,       setZoomOffset]       = useState(0);
  const [panelW,           setPanelW]           = useState(0);
  const [isPanMode,        setIsPanMode]        = useState(false);
  const [rawSvg,       setRawSvg]       = useState('');
  useEffect(() => { fetch('/images/stamp.svg').then(r => r.text()).then(setRawSvg); }, []);
  const zoom = Math.max(0.3, Math.min(autoZoom + zoomOffset, 2.0));
  const [isExportView, setIsExportView] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Auto-zoom for the fullscreen overlay
  const [fsAutoZoom, setFsAutoZoom] = useState(1.0);
  const [fsZoomOffset, setFsZoomOffset] = useState(0);
  const fsZoom = Math.max(0.3, Math.min(fsAutoZoom + fsZoomOffset, 2.0));

  // Hotkeys for zoom + fullscreen close
  useHotkeys([
    ['mod+=', () => setZoomOffset(o => o + 0.1)],
    ['mod+-', () => setZoomOffset(o => o - 0.1)],
    ['mod+0', () => setZoomOffset(0)],
    ['Escape', () => setIsFullscreen(false)],
  ]);

  // Ctrl+wheel zoom on normal panel
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const handler = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setZoomOffset(o => Math.max(-1.5, Math.min(o + (e.deltaY > 0 ? -0.1 : 0.1), 1.5)));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  // Ctrl+wheel zoom on fullscreen panel
  useEffect(() => {
    if (!isFullscreen) return;
    const el = fullscreenPanelRef.current;
    if (!el) return;
    const handler = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setFsZoomOffset(o => Math.max(-1.5, Math.min(o + (e.deltaY > 0 ? -0.1 : 0.1), 1.5)));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [isFullscreen]);

  // ── Pan mode: keep ref in sync and auto-enable when zoomed in ───────────
  useEffect(() => { isPanModeRef.current = isPanMode; }, [isPanMode]);
  useEffect(() => { if (zoomOffset > 0) setIsPanMode(true); }, [zoomOffset]);

  // ── Cursor update when pan mode changes ──────────────────────────────────
  useEffect(() => {
    const el = panelRef.current;
    if (el) el.style.cursor = isPanMode ? 'grab' : '';
  }, [isPanMode]);

  useEffect(() => {
    if (!isFullscreen) return;
    const el = fullscreenPanelRef.current;
    if (el) el.style.cursor = isPanMode ? 'grab' : '';
  }, [isPanMode, isFullscreen]);

  // ── Mouse drag/pan for normal panel ─────────────────────────────────────
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;

    const onDown = (e) => {
      if (e.button !== 0 || !isPanModeRef.current) return;
      dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
      el.style.cursor = 'grabbing';
    };
    const onMove = (e) => {
      if (!dragRef.current.active) return;
      el.scrollLeft = dragRef.current.sl - (e.clientX - dragRef.current.startX);
      el.scrollTop  = dragRef.current.st  - (e.clientY - dragRef.current.startY);
    };
    const onUp = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;
      el.style.cursor = isPanModeRef.current ? 'grab' : '';
    };

    el.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      el.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // ── Mouse drag/pan for fullscreen panel ──────────────────────────────────
  useEffect(() => {
    if (!isFullscreen) return;
    let cleanup;
    const t = setTimeout(() => {
      const el = fullscreenPanelRef.current;
      if (!el) return;
      if (isPanModeRef.current) el.style.cursor = 'grab';

      const onDown = (e) => {
        if (e.button !== 0 || !isPanModeRef.current) return;
        fsDragRef.current = { active: true, startX: e.clientX, startY: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
        el.style.cursor = 'grabbing';
      };
      const onMove = (e) => {
        if (!fsDragRef.current.active) return;
        el.scrollLeft = fsDragRef.current.sl - (e.clientX - fsDragRef.current.startX);
        el.scrollTop  = fsDragRef.current.st  - (e.clientY - fsDragRef.current.startY);
      };
      const onUp = () => {
        if (!fsDragRef.current.active) return;
        fsDragRef.current.active = false;
        el.style.cursor = isPanModeRef.current ? 'grab' : '';
      };

      el.addEventListener('mousedown', onDown);
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      cleanup = () => {
        el.removeEventListener('mousedown', onDown);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
    }, 50);

    return () => { clearTimeout(t); cleanup?.(); };
  }, [isFullscreen]);

  // ── Load Lexical JSON from R2 and convert to base HTML ────────────────────
  useEffect(() => {
    const contentKey = templateDoc?.contentKey;
    if (!contentKey) {
      baseHtml.current = '';
      setDisplayHtml('');
      return;
    }

    setLoading(true);
    const url = `${R2_URL}/${contentKey}`;

    fetch(url)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(json => {
        baseHtml.current = lexicalToHtml(json);
        // CRITICAL: don't overwrite displayHtml if we're in edit mode — that would
        // clobber the seeded manual HTML and reset the user's caret to the top.
        if (!isEditModeRef.current) {
          setDisplayHtml(replacePlaceholders(baseHtml.current, formValuesRef.current, placeholdersRef.current));
        }
      })
      .catch(err => {
        console.error('DocPreviewPanel: failed load content', err);
        baseHtml.current = '';
        setDisplayHtml('');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateDoc?.contentKey, refreshKey]);

  // ── Seed displayHtml from initialHtml ONCE per doc (manual edit mode) ─────
  // CRITICAL: must not re-run on every keystroke or the cursor jumps to the top
  // (dangerouslySetInnerHTML rewrites the DOM and contentEditable loses caret).
  // ALSO: must not touch baseHtml.current — keep that as the original template so
  // placeholder auto-fill still works after the user exits manual edit mode.
  const lastSeededDocIdRef = useRef(null);
  useEffect(() => {
    if (initialHtml === null) {
      lastSeededDocIdRef.current = null;
      return;
    }
    const docId = templateDoc?.id;
    if (lastSeededDocIdRef.current === docId) return;
    setDisplayHtml(initialHtml);
    lastSeededDocIdRef.current = docId;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHtml, templateDoc?.id]);

  // Reset seed marker when exiting edit mode so re-entry re-seeds,
  // AND re-render the document with current form values via placeholder replacement.
  useEffect(() => {
    if (!isEditMode) {
      lastSeededDocIdRef.current = null;
      if (baseHtml.current) {
        setDisplayHtml(replacePlaceholders(baseHtml.current, formValuesRef.current, placeholdersRef.current));
      }
    }
  }, [isEditMode]);

  // ── Replace placeholders live as user fills the form (debounced 100ms) ───
  // Paused while edit mode is on so user edits are not overwritten
  useEffect(() => {
    if (!baseHtml.current || isEditMode) return;
    if (debounceId.current) clearTimeout(debounceId.current);
    debounceId.current = setTimeout(() => {
      setDisplayHtml(replacePlaceholders(baseHtml.current, formValues, placeholdersConfig));
    }, 100);
    return () => clearTimeout(debounceId.current);
  }, [formValues, placeholdersConfig, isEditMode]);

  // ── Scroll to active placeholder (accounts for zoom transform) ──────────
  useEffect(() => {
    if (!activeSlug || !panelRef.current) return;
    const container = panelRef.current;
    // Scope query to .pageWrap only — the hidden pdfExport div also contains
    // data-slug spans and appears first in the DOM, so an unscoped querySelector
    // would find the off-screen element at left:-9999px and compute wrong coords.
    const pageWrap = container.querySelector(`.${styles.pageWrap}`);
    if (!pageWrap) return;
    const el = pageWrap.querySelector(`[data-slug="${activeSlug}"]`);
    if (!el) return;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const scrollTarget = container.scrollTop + (elRect.top - containerRect.top) - container.clientHeight / 2 + elRect.height / 2;
    container.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' });
  }, [activeSlug]);

  // Page dimensions + margins come from the doc's metadata. The new editor writes
  // explicit px margins (marginTop / marginBottom / marginLeft / marginRight);
  // older docs may still have the old { pageSize, margin: 'normal' } shape, so
  // we fall back to the preset when explicit margins are missing.
  const md          = templateDoc?.metadata || {};
  const pageSizeKey = String(md.pageSize || 'A4').toUpperCase();
  const pageSize    = PAGE_SIZES[pageSizeKey] || PAGE_SIZES.A4;
  const legacyMargin = PAGE_MARGINS[String(md.margin || 'normal').toLowerCase()] || PAGE_MARGINS.normal;
  const pageMargin  = {
    top:    typeof md.marginTop    === 'number' ? md.marginTop    : legacyMargin.top,
    bottom: typeof md.marginBottom === 'number' ? md.marginBottom : legacyMargin.bottom,
    left:   typeof md.marginLeft   === 'number' ? md.marginLeft   : legacyMargin.left,
    right:  typeof md.marginRight  === 'number' ? md.marginRight  : legacyMargin.right,
  };
  const isStamp = showStamp !== undefined ? showStamp : md.stamp === true;
  const stampConf = STAMP_CONFIG[stampDenomination] || STAMP_CONFIG[50];
  const coloredSvg = useMemo(
    () => {
      if (!rawSvg) return '';
      return rawSvg
        .replace(/#cc6e65/gi, stampConf.color)
        .replace(/#e4d099/gi, stampConf.bgColor)
        .replace(/<svg\b/, '<svg style=”width:100%;height:auto;display:block”');
    },
    [rawSvg, stampConf.color, stampConf.bgColor]
  );
  // ── Serial number derived from doc id ────────────────────────────────────
  const stampSerial = useMemo(() => {
    if (!isStamp) return '';
    return 'কখ ০০০০০০০';
  }, [isStamp]);
  const stampJsx = isStamp && coloredSvg ? (
    <div className={styles.stampOverlay} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1, pointerEvents: 'none' }}>
      {/* SVG wrapper — position:relative so amount overlays are relative to SVG height */}
      <div style={{ margin: 20, marginBottom: 0, position: 'relative', lineHeight: 0 }}>
        <div dangerouslySetInnerHTML={{ __html: coloredSvg }} />
        {/* Left amount box */}
        <div style={{ position: 'absolute', top: '48%', left: '21%', transform: 'translate(-50%, -50%)', textAlign: 'center', zIndex: 2, pointerEvents: 'none' }}>
          <div style={{ fontSize: 38, fontWeight: 'bold', color: stampConf.color, lineHeight: 1 }}>৳{stampConf.amountBn}</div>
        </div>
        {/* Right amount box */}
        <div style={{ position: 'absolute', top: '48%', left: '79%', transform: 'translate(-50%, -50%)', textAlign: 'center', zIndex: 2, pointerEvents: 'none' }}>
          <div style={{ fontSize: 38, fontWeight: 'bold', color: stampConf.color, lineHeight: 1 }}>৳{stampConf.amountBn}</div>
        </div>
        {/* Bottom kothay box */}
        <div style={{ position: 'absolute', top: '90%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', zIndex: 2, pointerEvents: 'none' }}>
          <div style={{ fontSize: 28, fontWeight: 'bold', color: stampConf.color, lineHeight: 1 }}>{stampConf.words}</div>
        </div>
        {/* SAMPLE watermark — makes clear the stamp is not a genuine one */}
        <div style={{ position: 'absolute', top: '46%', left: '50%', transform: 'translate(-50%, -50%) rotate(-20deg)', fontSize: 56, fontWeight: 'bold', letterSpacing: '0.2em', color: stampConf.color, opacity: 0.3, zIndex: 3, pointerEvents: 'none', whiteSpace: 'nowrap' }}>SAMPLE</div>
      </div>
      {/* Serial — left-aligned below the stamp image, aligned to stamp left edge */}
      <div style={{ textAlign: 'left', fontSize: 12, fontWeight: 'bold', padding: '2px 0', paddingLeft: 30, lineHeight: 1.4 }}>
        <span style={{ color: stampConf.color }}>কখ </span>
        <span style={{ color: '#1a1a1a', letterSpacing: '0.15em' }}>০০০০০০০</span>
      </div>
      <div style={{ position: 'absolute', bottom: 16, left: 0, width: '100%', textAlign: 'center', fontWeight: 'bold', fontSize: 26, color: stampConf.color, zIndex: 2, pointerEvents: 'none' }}>
        {STAMP_BOTTOM_TEXT}
      </div>
    </div>
  ) : null;
  const stampExportHtml = useMemo(() => {
    if (!isStamp || !coloredSvg) return '';
    const ac = stampConf.color;
    const c  = stampConf.color;
    return `<div style=”position:absolute;top:0;left:0;right:0;bottom:0;z-index:1;pointer-events:none”>`
      + `<div style=”margin:20px;position:relative;line-height:0”>${coloredSvg}`
      + `<div style=”position:absolute;top:48%;left:21%;transform:translate(-50%,-50%);text-align:center;z-index:2”><div style=”font-size:38px;font-weight:bold;color:${ac};line-height:1”>৳${stampConf.amountBn}</div></div>`
      + `<div style=”position:absolute;top:48%;left:79%;transform:translate(-50%,-50%);text-align:center;z-index:2”><div style=”font-size:38px;font-weight:bold;color:${ac};line-height:1”>৳${stampConf.amountBn}</div></div>`
      + `<div style=”position:absolute;top:90%;left:50%;transform:translate(-50%,-50%);text-align:center;z-index:2”><div style=”font-size:28px;font-weight:bold;color:${ac};line-height:1”>${stampConf.words}</div></div>`
      + `<div style=”position:absolute;top:46%;left:50%;transform:translate(-50%,-50%) rotate(-20deg);font-size:56px;font-weight:bold;letter-spacing:0.2em;color:${ac};opacity:0.3;z-index:3”>SAMPLE</div>`
      + `</div>`
      + `<div style=”text-align:left;font-size:12px;font-weight:bold;padding:2px 0 2px 20px;line-height:1.4”><span style=”color:${c}”>কখ </span><span style=”color:#1a1a1a;letter-spacing:0.15em”>০০০০০০০</span></div>`
      + `<div style=”position:absolute;bottom:28px;left:0;width:100%;text-align:center;font-weight:bold;font-size:20px;color:${ac};z-index:2”>${STAMP_BOTTOM_TEXT}</div>`
      + `</div>`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStamp, coloredSvg, stampSerial, stampConf.color, stampConf.color, stampConf.amountBn, stampConf.words]);

  // ── Stamp paper background: cream + scattered hair-curl security lines ────
  const stampPageBg = useMemo(() => {
    if (!isStamp) return {};
    const W = pageSize.width;
    const H = pageSize.height;
    // Deterministic LCG so the pattern is stable across renders
    let s = 0xdeadbeef;
    const rand = () => { s = (Math.imul(s ^ (s >>> 16), 0x45d9f3b) >>> 0); return s / 4294967296; };
    const CURL_COLORS = [
      'rgba(160,120,40,0.22)',  // amber
      'rgba(160,120,40,0.22)',  // amber (weighted 2×)
      'rgba(180,50,50,0.20)',   // red
      'rgba(50,130,60,0.20)',   // green
    ];
    const paths = [];
    for (let i = 0; i < 320; i++) {
      const x = rand() * W;
      const y = rand() * H;
      const len = 5 + rand() * 5;            // 5–10 px
      const a   = rand() * Math.PI * 2;
      const bend = 0.28 + rand() * 0.28;
      const ca = Math.cos(a), sa = Math.sin(a);
      const pca = -sa, psa = ca;             // perpendicular
      const cp1x = x  + len * 0.5 * ca + len * bend * pca;
      const cp1y = y  + len * 0.5 * sa + len * bend * psa;
      const mx   = x  + len * ca,  my = y + len * sa;
      const cp2x = mx + len * 0.5 * ca - len * bend * pca;
      const cp2y = my + len * 0.5 * sa - len * bend * psa;
      const ex   = mx + len * ca,  ey = my + len * sa;
      const sw   = (0.3 + rand() * 0.45).toFixed(2);
      const stroke = CURL_COLORS[(i * 7 + 3) % CURL_COLORS.length];
      paths.push(`<path d="M${x|0},${y|0} Q${cp1x|0},${cp1y|0} ${mx|0},${my|0} Q${cp2x|0},${cp2y|0} ${ex|0},${ey|0}" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`);
    }
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}">${paths.join('')}</svg>`;
    return {
      '--page-bg-color': '#fffbf2',
      '--page-bg-image': `url("data:image/svg+xml;base64,${btoa(svg)}")`,
      '--page-bg-size': `${W}px ${H}px`,
    };
  }, [isStamp, pageSize.width, pageSize.height]);

  // ── Canvas auto-zoom: fit page width to container, rescale on resize ────────
  const recalcZoom = useCallback(() => {
    const el = panelRef.current;
    if (!el) return;
    const w = el.clientWidth;
    setPanelW(w);
    const isMobileView = w < 768;
    const available = w - (isMobileView ? 12 : 48);
    // Side-by-side kicks in when panel is wide enough for ~2 pages at ~0.65× zoom
    const canSideBySide = sideBySide && w >= pageSize.width + 300;
    const targetWidth = canSideBySide ? pageSize.width * 2 + 32 : pageSize.width;
    const fitted = available / targetWidth;
    setAutoZoom(Math.max(0.15, Math.min(fitted, 1.5)));
  }, [pageSize.width, sideBySide]);

  useEffect(() => {
    recalcZoom();
    const el = panelRef.current;
    if (!el) return;
    const ro = new ResizeObserver(recalcZoom);
    ro.observe(el);
    return () => ro.disconnect();
  }, [recalcZoom]);

  // Auto-zoom for fullscreen panel
  const recalcFsZoom = useCallback(() => {
    const el = fullscreenPanelRef.current;
    if (!el) return;
    const available = el.clientWidth - 96;
    const fitted = available / pageSize.width;
    setFsAutoZoom(Math.max(0.5, Math.min(fitted, 1.5)));
  }, [pageSize.width]);

  useEffect(() => {
    if (!isFullscreen) return;
    // Small delay so the Portal has rendered and the ref is attached
    const t = setTimeout(() => {
      recalcFsZoom();
      const el = fullscreenPanelRef.current;
      if (!el) return;
      const ro = new ResizeObserver(recalcFsZoom);
      ro.observe(el);
      return () => ro.disconnect();
    }, 50);
    return () => clearTimeout(t);
  }, [isFullscreen, recalcFsZoom]);

  // Split into visual pages
  const pages = useMemo(() => splitIntoPages(displayHtml), [displayHtml]);

  // Shared page dimensions/padding applied as inline style on both visual pages and export div
  const pageSizeStyle = {
    width:          pageSize.width,
    paddingTop:     pageMargin.top,
    paddingBottom:  pageMargin.bottom,
    paddingLeft:    pageMargin.left,
    paddingRight:   pageMargin.right,
  };

  // Preview pages get a fixed height + clipping so overflow content is hidden exactly
  // like the final PDF — the export div must NOT use this (html2pdf needs full height).
  const previewPageStyle = {
    ...pageSizeStyle,
    height:    pageSize.height,
    minHeight: pageSize.height,
    overflow:  'hidden',
  };

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={styles.panel}>
        <div className={styles.scrollArea}>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
            <div className={styles.pageWrap}>
              <div className={styles.page} style={pageSizeStyle}>
                <div className="flex flex-col gap-2">
                  {[60, 90, 85, 80, 88, 75, 92, 70, 83, 50, 88, 76, 90].map((w, i) => (
                    <div
                      key={i}
                      className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded"
                      style={{ height: i % 6 === 0 ? 18 : 14, width: `${w}%` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.scrollArea} ref={panelRef}>
        {/* Hidden export target — per-page wrappers so page-number overlays
            are positioned relative to each individual page, not the whole doc */}
        <div ref={pageRef} style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          {pages.map((pageHtml, i) => {
            const isFirst = i === 0;
            const isLast  = i === pages.length - 1;
            const pageNum = i + 1;
            const showNums = isStamp && pages.length > 1;
            return (
              <div
                key={i}
                className={`${styles.page} ${styles.pdfExport}`}
                style={{
                  ...pageSizeStyle,
                  ...stampPageBg,
                  height:    pageSize.height,
                  minHeight: pageSize.height,
                  boxShadow: 'none',
                  backgroundColor: isStamp ? undefined : 'var(--mantine-color-body)',
                  boxSizing: 'border-box',
                  position:  'relative',
                  overflow:  'hidden',
                  pageBreakAfter: isLast ? 'auto' : 'always',
                  breakAfter:     isLast ? 'auto' : 'page',
                }}
              >
                {showNums && !isFirst && (
                  <div style={{ ...PAGE_NUM_STYLE, top: Math.max(8, pageMargin.top + 12) }}>পাতা নং-{toBn(pageNum)}</div>
                )}
                {stampJsx}
                <div dangerouslySetInnerHTML={{ __html: pageHtml || '' }} />
                {showNums && !isLast && (
                  <div style={{ ...PAGE_NUM_STYLE, bottom: 60 }}>চলমান-{toBn(pageNum + 1)}</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Visual multi-page preview with zoom scaling */}
        {(() => {
          const isSideBySide = sideBySide && pages.length > 1 && panelW >= pageSize.width + 300;
          const wrapWidth = isSideBySide ? pageSize.width * 2 + 32 : pageSize.width;
          const rowCount = isSideBySide ? Math.ceil(pages.length / 2) : pages.length;
          const totalScaledH = rowCount * pageSize.height + (rowCount - 1) * 32;
          // Outer div sized to the VISUAL width after scale so scrollArea gets a scrollbar
          return (
        <div style={{ width: '100%', minWidth: Math.ceil(wrapWidth * zoom), display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
        <div
          className={styles.pageWrap}
          style={{
            width: wrapWidth,
            transform: `scale(${zoom})`,
            transformOrigin: 'top center',
            marginBottom: `${(zoom - 1) * totalScaledH}px`,
            flexDirection: isSideBySide ? 'row' : 'column',
            flexWrap: isSideBySide ? 'wrap' : 'nowrap',
          }}
        >
          {pages.map((pageHtml, i) => {
            const isFirst = i === 0;
            const isLast  = i === pages.length - 1;
            const pageNum = i + 1;
            const showNums = isStamp && pages.length > 1;
            return (
              <div
                key={i}
                className={`${styles.page} ${isExportView ? styles.pdfExport : ''} ${isEditMode ? styles.editMode : ''} ${hideHighlights ? styles.noHighlights : ''}`}
                style={{ ...previewPageStyle, ...stampPageBg, position: 'relative' }}
              >
                {showNums && !isFirst && (
                  <div style={{ ...PAGE_NUM_STYLE, top: Math.max(8, pageMargin.top + 12) }}>পাতা নং-{toBn(pageNum)}</div>
                )}
                {stampJsx}
                <div
                  ref={el => {
                    if (i === 0) editContentRef.current = el;
                    pageRefsArr.current[i] = el;
                  }}
                  contentEditable={isEditMode || undefined}
                  suppressContentEditableWarning={isEditMode}
                  onInput={isEditMode ? handleEditInput : undefined}
                  onClick={isEditMode ? handlePlaceholderClick : undefined}
                  style={isEditMode ? { outline: 'none', cursor: 'text' } : undefined}
                  dangerouslySetInnerHTML={{
                    __html: pageHtml || (i === 0
                      ? '<p style="color:#aaa;text-align:center;margin-top:60px">Fill in the form to see the preview.</p>'
                      : '')
                  }}
                />
                {showNums && !isLast && (
                  <div style={{ ...PAGE_NUM_STYLE, bottom: 60 }}>চলমান-{toBn(pageNum + 1)}</div>
                )}
              </div>
            );
          })}
        </div>
        </div>
          );
        })()}
      </div>

      {/* Floating Toolbar */}
      {!hideToolbar && (
        <div
          className={cn(styles.toolbar, 'flex items-center gap-1 p-1 rounded-full border border-zinc-200 dark:border-zinc-700 bg-white/90 dark:bg-zinc-900/90 backdrop-blur shadow-md')}
          style={isEditMode ? { bottom: 80 } : undefined}
        >
          <TbBtn title={t('docPreview.zoomOutHint')} onClick={() => setZoomOffset(o => o - 0.1)}><IconZoomOut size={18} /></TbBtn>
          <TbBtn title={t('docPreview.resetZoomHint')} onClick={() => setZoomOffset(0)}><IconRotate size={16} /></TbBtn>
          <TbBtn title={t('docPreview.zoomInHint')} onClick={() => setZoomOffset(o => o + 0.1)}><IconZoomIn size={18} /></TbBtn>
          <div className="w-px h-4 bg-zinc-200 dark:bg-zinc-700 mx-1" />
          <TbBtn title={isPanMode ? t('docPreview.panModeOn') : t('docPreview.panModeOff')} onClick={() => setIsPanMode(m => !m)} active={isPanMode}>
            <IconHandGrab size={18} />
          </TbBtn>
          {!hideExportToggle && (
            <>
              <div className="w-px h-4 bg-zinc-200 dark:bg-zinc-700 mx-1" />
              <TbBtn title={isExportView ? t('docPreview.showHelpHighlights') : t('docPreview.showExportPreview')} onClick={() => setIsExportView(v => !v)} active={isExportView}>
                {isExportView ? <IconEye size={18} /> : <IconEyeOff size={18} />}
              </TbBtn>
            </>
          )}
          <div className="w-px h-4 bg-zinc-200 dark:bg-zinc-700 mx-1" />
          <TbBtn title={t('docPreview.fullscreenPreview')} onClick={() => { setIsFullscreen(true); setFsZoomOffset(0); }} active={isFullscreen}>
            <IconArrowsMaximize size={18} />
          </TbBtn>
        </div>
      )}

      {/* ── Fullscreen overlay ───────────────────────────────────────────── */}
      {isFullscreen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: '#2c2c2c',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Scrollable page area */}
            <div
              ref={fullscreenPanelRef}
              style={{
                flex: 1, overflowY: 'auto', overflowX: 'hidden',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: '48px 24px',
              }}
            >
              <div
                style={{
                  width: pageSize.width,
                  transform: `scale(${fsZoom})`,
                  transformOrigin: 'top center',
                  flexShrink: 0,
                  display: 'flex', flexDirection: 'column', gap: 32,
                  marginBottom: `${(fsZoom - 1) * (pages.length * pageSize.height + (pages.length - 1) * 32)}px`,
                  transition: 'transform 0.2s ease-out',
                }}
              >
                {pages.map((pageHtml, i) => {
                  const isFirst = i === 0;
                  const isLast  = i === pages.length - 1;
                  const pageNum = i + 1;
                  const showNums = isStamp && pages.length > 1;
                  return (
                    <div
                      key={i}
                      className={`${styles.page} ${styles.fullscreenPage}`}
                      style={{ ...previewPageStyle, ...stampPageBg, position: 'relative' }}
                    >
                      {showNums && !isFirst && (
                        <div style={{ ...PAGE_NUM_STYLE, top: Math.max(8, pageMargin.top - 28) }}>পাতা নং-{toBn(pageNum)}</div>
                      )}
                      {stampJsx}
                      <div
                        dangerouslySetInnerHTML={{
                          __html: pageHtml || (i === 0
                            ? '<p style="color:#aaa;text-align:center;margin-top:60px">Fill in the form to see the preview.</p>'
                            : '')
                        }}
                      />
                      {showNums && !isLast && (
                        <div style={{ ...PAGE_NUM_STYLE, bottom: 8 }}>চলমান-{toBn(pageNum + 1)}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Fullscreen toolbar */}
            <div style={{ position: 'absolute', bottom: 24, right: 24, zIndex: 10000 }}
              className="flex items-center gap-1 p-1 rounded-full border border-zinc-200 dark:border-zinc-700 bg-white/90 dark:bg-zinc-900/88 backdrop-blur shadow-md">
              <TbBtn title={t('docPreview.zoomOut')} onClick={() => setFsZoomOffset(o => o - 0.1)}><IconZoomOut size={18} /></TbBtn>
              <TbBtn title={t('docPreview.resetZoom')} onClick={() => setFsZoomOffset(0)}><IconRotate size={16} /></TbBtn>
              <TbBtn title={t('docPreview.zoomIn')} onClick={() => setFsZoomOffset(o => o + 0.1)}><IconZoomIn size={18} /></TbBtn>
              <div className="w-px h-4 bg-zinc-200 dark:bg-zinc-700 mx-1" />
              <TbBtn title={isPanMode ? t('docPreview.panModeOn') : t('docPreview.panModeOff')} onClick={() => setIsPanMode(m => !m)} active={isPanMode}>
                <IconHandGrab size={18} />
              </TbBtn>
              <div className="w-px h-4 bg-zinc-200 dark:bg-zinc-700 mx-1" />
              <TbBtn title={t('docPreview.exitFullscreen')} onClick={() => setIsFullscreen(false)} active>
                <IconArrowsMinimize size={18} />
              </TbBtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
